import { HubSearchResult } from '../../types/hubSearch';
import { ProposalBuilderData, EnhancedDeal, ExecInboxThread } from '../../types';
import { VendorOrgDirectoryRow } from '../vendorOrgSummary';
import { scoreMultiFieldMatch } from '../search';
import { Lead } from '../../types';

export interface MessageConversation {
  leadId: string;
  lead: Lead;
  lastMessage: { content: string };
}

export function searchProposalsHub(
  query: string,
  proposals: (ProposalBuilderData & { id: string })[],
  limit = 20,
): HubSearchResult[] {
  const q = query.trim();
  if (!q) {
    return proposals.slice(0, 12).map(p => ({
      id: p.id,
      kind: 'proposal',
      section: 'Proposals',
      title: p.clientName || p.slug || 'Untitled',
      subtitle: [p.status, p.isKitProposal ? 'Kit' : 'Custom'].filter(Boolean).join(' · '),
      matchScore: 1,
      payload: p,
    }));
  }
  return (
    proposals
      .map((p): HubSearchResult | null => {
        const score = scoreMultiFieldMatch(q, [
          { text: p.clientName, weight: 10 },
          { text: p.slug, weight: 9 },
          { text: p.status, weight: 3 },
        ]);
        if (score <= 0) return null;
        return {
          id: p.id,
          kind: 'proposal',
          section: 'Proposals',
          title: p.clientName || p.slug || 'Untitled',
          subtitle: p.slug || '',
          matchScore: score,
          payload: p,
        };
      })
      .filter((r) => r !== null) as HubSearchResult[]
  )
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, limit);
}

export function searchVendorsHub(
  query: string,
  rows: VendorOrgDirectoryRow[],
  limit = 20,
): HubSearchResult[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return rows.slice(0, 12).map(row => ({
      id: row.orgId,
      kind: 'vendor',
      section: 'Vendors',
      title: row.org.name,
      subtitle: row.capabilities.join(', '),
      matchScore: 1,
      payload: row,
    }));
  }
  return rows
    .filter(row => row.searchText.includes(q))
    .slice(0, limit)
    .map(row => ({
      id: row.orgId,
      kind: 'vendor',
      section: 'Vendors',
      title: row.org.name,
      subtitle: row.org.defaultContactEmail || '',
      matchScore: row.searchText.includes(q) ? 0.95 : 0.5,
      payload: row,
    }));
}

export function searchExecutiveHub(
  query: string,
  threads: ExecInboxThread[],
  limit = 20,
): HubSearchResult[] {
  const q = query.trim();
  if (!q) {
    return threads.slice(0, 12).map(t => ({
      id: t.threadId,
      kind: 'inboxThread',
      section: 'Inbox',
      title: t.subject || '(no subject)',
      subtitle: [t.lastFromName || t.lastFrom, t.snippet].filter(Boolean).join(' · '),
      matchScore: 1,
      payload: t,
    }));
  }
  return (
    threads
      .map((t): HubSearchResult | null => {
        const score = scoreMultiFieldMatch(q, [
          { text: t.subject, weight: 10 },
          { text: t.lastFrom, weight: 6 },
          { text: t.lastFromName, weight: 6 },
          { text: t.snippet, weight: 5 },
        ]);
        if (score <= 0) return null;
        return {
          id: t.threadId,
          kind: 'inboxThread',
          section: 'Inbox',
          title: t.subject || '(no subject)',
          subtitle: t.snippet || '',
          matchScore: score,
          payload: t,
        };
      })
      .filter((r) => r !== null) as HubSearchResult[]
  )
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, limit);
}

export function searchMessagesHub(
  query: string,
  conversations: MessageConversation[],
  limit = 20,
): HubSearchResult[] {
  const q = query.trim();
  if (!q) {
    return conversations.slice(0, 12).map(conv => ({
      id: conv.leadId,
      kind: 'message',
      section: 'Conversations',
      title: `${conv.lead.firstName} ${conv.lead.lastName}`.trim(),
      subtitle: conv.lastMessage.content.slice(0, 80),
      matchScore: 1,
      payload: conv,
    }));
  }
  const lower = q.toLowerCase();
  return conversations
    .filter(conv => {
      const fullName = `${conv.lead.firstName} ${conv.lead.lastName}`.toLowerCase();
      return (
        fullName.includes(lower) ||
        conv.lead.email?.toLowerCase().includes(lower) ||
        conv.lead.phone?.includes(q) ||
        conv.lastMessage.content.toLowerCase().includes(lower)
      );
    })
    .slice(0, limit)
    .map(conv => ({
      id: conv.leadId,
      kind: 'message' as const,
      section: 'Conversations',
      title: `${conv.lead.firstName} ${conv.lead.lastName}`.trim(),
      subtitle: conv.lastMessage.content.slice(0, 80),
      matchScore: 0.9,
      payload: conv,
    }));
}

export function searchWonDealsHub(
  query: string,
  deals: EnhancedDeal[],
  limit = 20,
): HubSearchResult[] {
  const won = deals.filter(d => d.outcome === 'won');
  const q = query.trim();
  if (!q) {
    return won.slice(0, 12).map(d => ({
      id: d.id!,
      kind: 'wonDeal',
      section: 'Won deals',
      title: d.title,
      subtitle: d.contactName || '',
      matchScore: 1,
      payload: d,
    }));
  }
  return (
    won
      .map((d): HubSearchResult | null => {
        const score = scoreMultiFieldMatch(q, [
          { text: d.title, weight: 10 },
          { text: d.contactName, weight: 8 },
          { text: d.clientLabel, weight: 7 },
          { text: d.descriptor, weight: 7 },
          { text: d.projectNumber, weight: 9 },
          { text: d.company, weight: 5 },
        ]);
        if (score <= 0 || !d.id) return null;
        return {
          id: d.id,
          kind: 'wonDeal',
          section: 'Won deals',
          title: d.title,
          subtitle: d.contactName || '',
          matchScore: score,
          payload: d,
        };
      })
      .filter((r) => r !== null) as HubSearchResult[]
  )
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, limit);
}

export function searchDesignTrackerHub(
  query: string,
  deals: EnhancedDeal[],
  limit = 20,
): HubSearchResult[] {
  const design = deals.filter(d => d.stageId === 'design' && d.outcome === 'active');
  const q = query.trim();
  if (!q) {
    return design.slice(0, 12).map(d => ({
      id: d.id!,
      kind: 'designDeal',
      section: 'Design',
      title: d.title,
      subtitle: d.contactName || '',
      matchScore: 1,
      payload: d,
    }));
  }
  return (
    design
      .map((d): HubSearchResult | null => {
        const score = scoreMultiFieldMatch(q, [
          { text: d.title, weight: 10 },
          { text: d.contactName, weight: 8 },
          { text: d.clientLabel, weight: 7 },
          { text: d.descriptor, weight: 7 },
          { text: d.projectNumber, weight: 9 },
        ]);
        if (score <= 0 || !d.id) return null;
        return {
          id: d.id,
          kind: 'designDeal',
          section: 'Design',
          title: d.title,
          subtitle: d.contactName || '',
          matchScore: score,
          payload: d,
        };
      })
      .filter((r) => r !== null) as HubSearchResult[]
  )
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, limit);
}
